import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import SelectField from '../common/SelectField.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import Loader from '../common/Loader.js';
import companyService from '../../services/CompanyService.js';
import companySubscriptionService from '../../services/CompanySubscriptionService.js';
import { CreditCard, Plus, Calendar, DollarSign, FileText, Camera } from "lucide-react";
import { validators, inputFilters, useValidation, ValidationMessage } from '../common/Validation.js';
import Button from '../common/Button.js';
import { companySelectLabel } from '../../utils/companyFeatures.js';
import { useTranslation } from 'react-i18next';


const AddCompanySubscription = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const companyIdParam = searchParams.get('companyId');
  
  const [activeTab, setActiveTab] = useState('subscription');
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [createdSubscription, setCreatedSubscription] = useState(null);
  
  // Subscription form data
  const [subscriptionData, setSubscriptionData] = useState({
    company_id: companyIdParam || '',
    period_type: 'one_month',
    custom_years: '',
    from_date: new Date().toISOString().split('T')[0],
    to_date: companySubscriptionService.calculateEndDate(
      new Date().toISOString().split('T')[0],
      'one_month'
    ),
    subscription_amount: '',
    notes: '',
    start_from_existing: false
  });

  // Payment form data
  const [paymentData, setPaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    payment_status: 'completed',
    payment_date: new Date().toISOString().split('T')[0],
    transaction_id: '',
    transaction_reference: '',
    bank_name: '',
    cheque_number: '',
    upi_id: '',
    notes: '',
    remarks: ''
  });

  const [receipt, setReceipt] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);
  const navigate = useNavigate();

  const { errors: subErrors, validate: validateSub, validateField: validateSubField } = useValidation();
  const { errors: payErrors, validate: validatePay, validateField: validatePayField } = useValidation();

  const subRules = {
    company_id:          (v) => validators.select(v, 'company'),
    from_date:           (v) => validators.required(v, 'From date'),
    to_date:             (v) => validators.required(v, 'To date'),
    subscription_amount: (v) => validators.required(v, 'Subscription amount') || validators.number(v, { min: 0.01, label: 'Subscription amount' }),
    custom_years:        (v) => subscriptionData.period_type === 'custom_years' ? (validators.required(v, 'Custom years') || validators.number(v, { min: 1, label: 'Custom years' })) : '',
  };

  const payRules = {
    amount:       (v) => validators.required(v, 'Amount') || validators.number(v, { min: 0.01, label: 'Amount' }),
    payment_date: (v) => validators.required(v, 'Payment date'),
  };

  useEffect(() => {
    fetchCompanies();
  }, []);

  const suggestToDate = (fromDate, periodType, customYears) => {
    if (!fromDate || !periodType) return '';
    const customYearsValue = customYears ? parseInt(customYears, 10) : null;
    return companySubscriptionService.calculateEndDate(fromDate, periodType, customYearsValue);
  };
  
  // Sync payment amount with subscription amount
  useEffect(() => {
    if (subscriptionData.subscription_amount) {
      setPaymentData(prev => ({ ...prev, amount: subscriptionData.subscription_amount }));
    }
  }, [subscriptionData.subscription_amount]);

  const fetchCompanies = async () => {
    try {
      const response = await companyService.getAllForSelect();
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Error fetching companies:', err);
      Alert.error('Failed to load companies');
    } finally {
      setLoadingCompanies(false);
    }
  };

  const handleSubscriptionChange = (e) => {
    const { name, value, type, checked } = e.target;

    let filtered = type === 'checkbox' ? checked : value;
    if (name === 'subscription_amount') filtered = inputFilters.amount(value);
    else if (name === 'custom_years') filtered = inputFilters.digitsOnly(value);

    setSubscriptionData(prev => {
      const next = { ...prev, [name]: filtered };
      if (name === 'from_date' || name === 'period_type' || name === 'custom_years') {
        next.to_date = suggestToDate(next.from_date, next.period_type, next.custom_years);
      }
      return next;
    });

    if (subRules[name]) {
      validateSubField(name, filtered, subRules[name]);
    }
  };

  const handlePaymentChange = (e) => {
    const { name, value } = e.target;

    let filtered = value;
    if (name === 'amount') filtered = inputFilters.amount(value);

    setPaymentData(prev => ({ ...prev, [name]: filtered }));

    if (payRules[name]) {
      validatePayField(name, filtered, payRules[name]);
    }
  };

  const handleReceiptChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        Alert.error('Receipt file must be less than 5MB');
        return;
      }
      setReceipt(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setReceiptPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeReceipt = () => {
    setReceipt(null);
    setReceiptPreview(null);
    const fileInput = document.getElementById('receipt-input');
    if (fileInput) fileInput.value = '';
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();

    if (!validateSub(subscriptionData, subRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }
    if (subscriptionData.to_date < subscriptionData.from_date) {
      Alert.error('To date cannot be before from date');
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        ...subscriptionData,
        subscription_amount: parseFloat(subscriptionData.subscription_amount),
        custom_years: subscriptionData.period_type === 'custom_years' ? parseInt(subscriptionData.custom_years) : null
      };

      const response = await companySubscriptionService.create(dataToSend);
      setCreatedSubscription(response.data);
      Alert.success('Subscription created successfully!');
      setActiveTab('payment');
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!createdSubscription) {
      Alert.error('Please create subscription first');
      setActiveTab('subscription');
      return;
    }

    if (!validatePay(paymentData, payRules)) {
      Alert.error('Please fix the highlighted errors before submitting');
      return;
    }

    setLoading(true);

    try {
      const dataToSend = {
        ...paymentData,
        subscription_id: createdSubscription.id,
        company_id: subscriptionData.company_id,
        amount: parseFloat(paymentData.amount)
      };

      await companySubscriptionService.createPayment(dataToSend, receipt);
      Alert.success('Payment added successfully!');
      setTimeout(() => navigate(`/company-subscriptions/view/${createdSubscription.id}`), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to add payment');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPayment = () => {
    if (createdSubscription) {
      navigate(`/company-subscriptions/view/${createdSubscription.id}`);
    }
  };

  return (
    <DashboardLayout>
      {loadingCompanies ? (
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading companies..." />
        </div>
      ) : (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6 md:p-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-gray-200">
                  <div className="p-2 bg-orange-100 rounded-xl">
                    <CreditCard className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-700">Add New Subscription</h2>
                    <p className="text-xs sm:text-sm text-gray-600 mt-1">Create a new company subscription</p>
                  </div>
                </div>

                {/* Tabs */}
                <div className="mb-6">
                  <div className="border-b border-gray-200">
                    <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                      <button
                        onClick={() => setActiveTab('subscription')}
                        className={`${
                          activeTab === 'subscription'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                      >
                        <Calendar className="w-4 h-4" />
                        Subscription Details
                        {createdSubscription && (
                          <span className="ml-2 bg-green-100 text-green-800 text-xs font-semibold px-2 py-0.5 rounded">
                            ✓
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => {
                          if (!createdSubscription) {
                            Alert.warning('Please create subscription first');
                            return;
                          }
                          setActiveTab('payment');
                        }}
                        className={`${
                          activeTab === 'payment'
                            ? 'border-orange-500 text-orange-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
                        disabled={!createdSubscription}
                      >
                        <DollarSign className="w-4 h-4" />
                        Payment Details
                      </button>
                    </nav>
                  </div>
                </div>

                {/* Tab Content */}
                {activeTab === 'subscription' && (
                  <form onSubmit={handleSubscriptionSubmit} className="space-y-6">
                    {/* Subscription Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Subscription Information</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Company <span className="text-danger font-bold">*</span>
                          </label>
                          <SelectField variant="form"
                            name="company_id"
                            value={subscriptionData.company_id}
                            onChange={handleSubscriptionChange}
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${subErrors.company_id ? 'border-red-400' : 'border-gray-300'}`}
                            required
                            disabled={!!createdSubscription}
                          >
                            <option value="">Select Company</option>
                            {companies.map(company => (
                              <option key={company.id} value={company.id}>
                                {companySelectLabel(company)}
                              </option>
                            ))}
                          </SelectField>
                          <ValidationMessage error={subErrors.company_id} />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Period Type <span className="text-danger font-bold">*</span>
                          </label>
                          <SelectField variant="form"
                            name="period_type"
                            value={subscriptionData.period_type}
                            onChange={handleSubscriptionChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            required
                            disabled={!!createdSubscription}
                          >
                            <option value="seven_days">7 Days</option>
                            <option value="one_month">1 Month</option>
                            <option value="three_months">3 Months</option>
                            <option value="six_months">6 Months</option>
                            <option value="twelve_months">12 Months</option>
                            <option value="custom_years">Custom Years</option>
                          </SelectField>
                        </div>

                        {subscriptionData.period_type === 'custom_years' && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Custom Years <span className="text-danger font-bold">*</span>
                            </label>
                            <input
                              type="text"
                              name="custom_years"
                              value={subscriptionData.custom_years}
                              onChange={handleSubscriptionChange}
                              placeholder="Enter number of years"
                              className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${subErrors.custom_years ? 'border-red-400' : 'border-gray-300'}`}
                              required
                              disabled={!!createdSubscription}
                            />
                            <ValidationMessage error={subErrors.custom_years} />
                          </div>
                        )}

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            From Date <span className="text-danger font-bold">*</span>
                          </label>
                          <input
                            type="date"
                            name="from_date"
                            value={subscriptionData.from_date}
                            onChange={handleSubscriptionChange}
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${subErrors.from_date ? 'border-red-400' : 'border-gray-300'}`}
                            required
                            disabled={!!createdSubscription}
                          />
                          <ValidationMessage error={subErrors.from_date} />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            To Date <span className="text-danger font-bold">*</span>
                          </label>
                          <input
                            type="date"
                            name="to_date"
                            value={subscriptionData.to_date}
                            onChange={handleSubscriptionChange}
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${subErrors.to_date ? 'border-red-400' : 'border-gray-300'}`}
                            required
                            disabled={!!createdSubscription}
                          />
                          <ValidationMessage error={subErrors.to_date} />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Subscription Amount <span className="text-danger font-bold">*</span>
                          </label>
                          <input
                            type="text"
                            name="subscription_amount"
                            value={subscriptionData.subscription_amount}
                            onChange={handleSubscriptionChange}
                            placeholder="Enter amount"
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${subErrors.subscription_amount ? 'border-red-400' : 'border-gray-300'}`}
                            required
                            disabled={!!createdSubscription}
                          />
                          <ValidationMessage error={subErrors.subscription_amount} />
                        </div>

                        <div className="col-span-2 sm:col-span-2 lg:col-span-3">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Notes
                          </label>
                          <textarea
                            name="notes"
                            value={subscriptionData.notes}
                            onChange={handleSubscriptionChange}
                            rows="3"
                            placeholder="Enter any additional notes"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            disabled={!!createdSubscription}
                          />
                        </div>

                        <div className="col-span-2 flex items-center">
                          <input
                            type="checkbox"
                            id="start_from_existing"
                            name="start_from_existing"
                            checked={subscriptionData.start_from_existing}
                            onChange={handleSubscriptionChange}
                            className="w-4 h-4 text-orange-600 focus:ring-blue-400 border-gray-300 rounded"
                            disabled={!!createdSubscription}
                          />
                          <label htmlFor="start_from_existing" className="ml-2 text-xs sm:text-sm text-gray-900">
                            Start from existing subscription end date
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Form Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 border-t">
                      {!createdSubscription ? (
                        <>
                          <Button variant="primary" type="submit" loading={loading}>
                            {loading ? 'Creating...' : 'Create Subscription & Continue'}
                          </Button>
                          <Button variant="secondary" onClick={() => navigate('/company-subscriptions')}>
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button variant="primary" onClick={() => setActiveTab('payment')}>
                            Continue to Payment
                          </Button>
                          <Button variant="secondary" onClick={handleSkipPayment}>
                            Skip Payment & View
                          </Button>
                        </>
                      )}
                    </div>
                  </form>
                )}

                {activeTab === 'payment' && createdSubscription && (
                  <form onSubmit={handlePaymentSubmit} className="space-y-6">
                    {/* Payment Details */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Payment Information</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-6">
                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Amount <span className="text-danger font-bold">*</span>
                          </label>
                          <input
                            type="text"
                            name="amount"
                            value={paymentData.amount}
                            onChange={handlePaymentChange}
                            placeholder="Enter payment amount"
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${payErrors.amount ? 'border-red-400' : 'border-gray-300'}`}
                            required
                          />
                          <ValidationMessage error={payErrors.amount} />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Payment Method <span className="text-danger font-bold">*</span>
                          </label>
                          <SelectField variant="form"
                            name="payment_method"
                            value={paymentData.payment_method}
                            onChange={handlePaymentChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            required
                          >
                            <option value="cash">Cash</option>
                            <option value="bank_transfer">Bank Transfer</option>
                            <option value="cheque">Cheque</option>
                            <option value="upi">UPI</option>
                            <option value="credit_card">Credit Card</option>
                            <option value="debit_card">Debit Card</option>
                            <option value="net_banking">Net Banking</option>
                            <option value="other">Other</option>
                          </SelectField>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Payment Status <span className="text-danger font-bold">*</span>
                          </label>
                          <SelectField variant="form"
                            name="payment_status"
                            value={paymentData.payment_status}
                            onChange={handlePaymentChange}
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            required
                          >
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                            <option value="refunded">Refunded</option>
                            <option value="cancelled">Cancelled</option>
                          </SelectField>
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Payment Date <span className="text-danger font-bold">*</span>
                          </label>
                          <input
                            type="date"
                            name="payment_date"
                            value={paymentData.payment_date}
                            onChange={handlePaymentChange}
                            className={`w-full px-3 sm:px-4 py-2 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base ${payErrors.payment_date ? 'border-red-400' : 'border-gray-300'}`}
                            required
                          />
                          <ValidationMessage error={payErrors.payment_date} />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Transaction ID
                          </label>
                          <input
                            type="text"
                            name="transaction_id"
                            value={paymentData.transaction_id}
                            onChange={handlePaymentChange}
                            placeholder="Enter transaction ID"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>

                        <div>
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Transaction Reference
                          </label>
                          <input
                            type="text"
                            name="transaction_reference"
                            value={paymentData.transaction_reference}
                            onChange={handlePaymentChange}
                            placeholder="Enter reference number"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>

                        {paymentData.payment_method === 'bank_transfer' && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Bank Name
                            </label>
                            <input
                              type="text"
                              name="bank_name"
                              value={paymentData.bank_name}
                              onChange={handlePaymentChange}
                              placeholder="Enter bank name"
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            />
                          </div>
                        )}

                        {paymentData.payment_method === 'cheque' && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              Cheque Number
                            </label>
                            <input
                              type="text"
                              name="cheque_number"
                              value={paymentData.cheque_number}
                              onChange={handlePaymentChange}
                              placeholder="Enter cheque number"
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            />
                          </div>
                        )}

                        {paymentData.payment_method === 'upi' && (
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                              UPI ID
                            </label>
                            <input
                              type="text"
                              name="upi_id"
                              value={paymentData.upi_id}
                              onChange={handlePaymentChange}
                              placeholder="Enter UPI ID"
                              className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                            />
                          </div>
                        )}

                        <div className="col-span-2 sm:col-span-2 lg:col-span-3">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Payment Notes
                          </label>
                          <textarea
                            name="notes"
                            value={paymentData.notes}
                            onChange={handlePaymentChange}
                            rows="2"
                            placeholder="Enter payment notes"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>

                        <div className="col-span-2 sm:col-span-2 lg:col-span-3">
                          <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">
                            Remarks
                          </label>
                          <textarea
                            name="remarks"
                            value={paymentData.remarks}
                            onChange={handlePaymentChange}
                            rows="2"
                            placeholder="Enter additional remarks"
                            className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm sm:text-base"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Receipt Upload */}
                    {/* Receipt Upload */}
<div>
  <h3 className="text-base sm:text-md font-semibold text-gray-700 mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
    Payment Receipt
  </h3>

  <div className="space-y-4">

    <label className="block text-xs sm:text-sm font-medium text-secondary mb-2">
      Upload Receipt (Optional)
    </label>

    <div className="flex gap-4 flex-wrap">

      {/* Upload Box */}
      {!receiptPreview && (
        <label
          htmlFor="receipt-input"
          className="w-48 h-32 border-2 border-dashed border-gray-300 rounded-md flex flex-col items-center justify-center cursor-pointer hover:border-orange-400 transition"
        >
          <Camera className="mx-auto w-8 h-8 text-gray-400 mb-2" />
          <span className="text-xs text-gray-500">Click to upload</span>
        </label>
      )}

      {/* Hidden Input */}
      <input
        type="file"
        id="receipt-input"
        accept="image/*,.pdf"
        onChange={handleReceiptChange}
        className="hidden"
      />

      {/* Preview Box */}
      {receiptPreview && (
        <div className="relative w-48 h-32 border rounded-md overflow-hidden">

          {/* Image Preview */}
          {receipt?.type?.startsWith('image/') ? (
            <img
              src={receiptPreview}
              alt="Receipt"
              className="w-full h-full object-cover"
            />
          ) : (
            /* PDF Preview */
            <div className="flex flex-col items-center justify-center h-full bg-gray-100">
              <FileText className="w-6 h-6 text-gray-600 mb-1" />
              <span className="text-xs text-gray-700 px-2 text-center">
                {receipt?.name}
              </span>
            </div>
          )}

          {/* Remove Button */}
          <button
            type="button"
            onClick={removeReceipt}
            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

        </div>
      )}

    </div>

    <p className="text-xs text-gray-500">
      Maximum file size: 5MB. Supported formats: JPG, PNG, PDF
    </p>

  </div>
</div>

                    {/* Form Actions */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-4 border-t">
                      <Button variant="primary" type="submit" loading={loading}>
                        {loading ? 'Adding Payment...' : 'Add Payment & Complete'}
                      </Button>
                      <Button variant="secondary" onClick={handleSkipPayment}>
                        Skip & View Subscription
                      </Button>
                    </div>
                  </form>
                )}
              </div>
      )}
    </DashboardLayout>
  );
};

export default AddCompanySubscription;